import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserService } from '../../../services/user.service';
import { ToastService } from '../../../services/toast.service';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { AuthService } from '../../../services/auth.service';
import { MaterialModule } from '../../../shared/material.module';
import { User, UpdateProfileRequest, ChangePasswordRequest, UserRole } from '../../../models/user.model';

export interface ProfileDialogData {
  user: User;
}

@Component({
  selector: 'app-profile-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './profile-dialog.component.html',
  styleUrl: './profile-dialog.component.scss'
})
export class ProfileDialogComponent implements OnInit {
  profileForm: FormGroup;
  passwordForm: FormGroup;
  user: User;
  isLoading = false;
  activeTab = 0;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private toastService: ToastService,
    private errorHandler: ErrorHandlerService,
    private authService: AuthService,
    public dialogRef: MatDialogRef<ProfileDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ProfileDialogData
  ) {
    this.user = data.user;
    
    this.profileForm = this.fb.group({
      firstName: [this.user.firstName, [Validators.required]],
      lastName: [this.user.lastName, [Validators.required]],
      email: [{ value: this.user.email, disabled: true }],
      phone: [this.user.phone || '', [Validators.pattern(/^[0-9]*$/)]],
      role: [{ value: this.user.role, disabled: true }],
      tenant: [{ value: this.user.tenant?.name || 'N/A', disabled: true }]
    });

    this.passwordForm = this.fb.group({
      currentPassword: ['', [Validators.required, Validators.minLength(6)]],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    // Form is already initialized in constructor
  }

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');
    
    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
    } else {
      confirmPassword?.setErrors(null);
    }
    
    return null;
  }

  onSaveProfile(): void {
    if (this.profileForm.valid) {
      this.isLoading = true;
      const updateData: UpdateProfileRequest = {
        firstName: this.profileForm.value.firstName,
        lastName: this.profileForm.value.lastName,
        phone: this.profileForm.value.phone || undefined
      };

      this.userService.updateProfile(updateData).subscribe({
        next: (updatedUser: User) => {
          this.toastService.success('Profile Updated', 'Your profile has been updated successfully');
          this.user = updatedUser;
          // Update the current user in AuthService
          this.authService.updateCurrentUser(updatedUser);
          this.isLoading = false;
          // Close the dialog after successful save
          this.dialogRef.close(updatedUser);
        },
        error: (error: any) => {
          this.isLoading = false;
          let errorMessage = 'Failed to update profile. Please try again.';
          
          // Handle specific error cases
          if (error.status === 401) {
            errorMessage = 'Authentication failed. Your session may have expired. Please log in again.';
          } else if (error.status === 403) {
            errorMessage = 'You do not have permission to update your profile.';
          } else if (error.status === 422) {
            // Validation errors
            const errorResult = this.errorHandler.handleError(error, this.profileForm);
            errorMessage = errorResult.message || errorMessage;
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          this.toastService.error('Error', errorMessage);
        }
      });
    }
  }

  onChangePassword(): void {
    if (this.passwordForm.valid) {
      this.isLoading = true;
      const changePasswordData: ChangePasswordRequest = {
        currentPassword: this.passwordForm.value.currentPassword,
        newPassword: this.passwordForm.value.newPassword
      };

      this.userService.changePassword(changePasswordData).subscribe({
        next: () => {
          this.toastService.success('Password Changed', 'Your password has been changed successfully');
          this.passwordForm.reset();
          this.isLoading = false;
        },
        error: (error: any) => {
          this.isLoading = false;
          let errorMessage = 'Failed to change password. Please try again.';
          
          // Handle specific error cases
          if (error.status === 401) {
            errorMessage = 'Authentication failed. Your session may have expired. Please log in again.';
          } else if (error.status === 403) {
            errorMessage = 'You do not have permission to change your password.';
          } else if (error.status === 400 || error.status === 422) {
            // Validation errors or bad request
            const errorResult = this.errorHandler.handleError(error, this.passwordForm);
            errorMessage = errorResult.message || errorMessage;
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          this.toastService.error('Error', errorMessage);
        }
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    // Remove all non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    
    // Update the form control value
    this.profileForm.patchValue({ phone: numericValue }, { emitEvent: false });
    
    // Update the input field value directly to reflect the change immediately
    input.value = numericValue;
  }

  getErrorMessage(field: string): string {
    const control = this.profileForm.get(field) || this.passwordForm.get(field);
    if (control?.hasError('required')) {
      return `${field} is required`;
    }
    if (control?.hasError('minlength')) {
      return `${field} must be at least 6 characters`;
    }
    if (control?.hasError('passwordMismatch')) {
      return 'Passwords do not match';
    }
    if (control?.hasError('pattern') && field === 'phone') {
      return 'Phone number must contain only numbers';
    }
    return '';
  }
}
